import { Fragment, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { apiCollections } from "services";
import { CollectionFeed } from "components/feed/CollectionFeed";
import { CollectionHeader } from "components/collections";
import { CollectionEditMenu, ShareMenu } from "components/menus";
import { DeleteCollectionPopup, EditCollectionPopup, EmbedCodePopup } from "components/popups";
import { useCollectionQuery } from "queries";
import { useUserName } from "hooks";
import { ICollection } from "services/types";
import { routes } from "routes";

import "../common.scss";
import "./CollectionItemsPage.scss";
import { FooterAds } from "components/ads/FooterAds";

const requireString = (value: string | undefined): string => {
  if (value === undefined) {
    throw new Error("required parameter not set");
  }

  return value;
};

export const CollectionItemsPage = () => {
  const params = useParams();
  const userName = requireString(params.userId);
  const collectionId = requireString(params.id);

  const currentUser = useUserName();
  const isEditable = currentUser === userName;
  const navigate = useNavigate();

  const [isMenuVisible, setMenuVisible] = useState<boolean>(false);
  const [isEditVisible, setEditVisible] = useState<boolean>(false);
  const [isDeleteCollectionVisible, setDeleteCollectionVisible] = useState<boolean>(false);
  const [isShareVisible, setShareVisible] = useState<boolean>(false);
  const [isEmbedVisible, setEmbedVisible] = useState<boolean>(false);
  const [isManage, setManage] = useState<boolean>(false);
  const [menuRef, setMenuRef] = useState<HTMLElement|null>(null);
  const [selection, setSelection] = useState<string[]>([]);
  const [collection, setCollection] = useState<ICollection|undefined>(undefined);

  const query = useCollectionQuery({
    userName,
    collectionId,
    showPrivate: isEditable,
  });

  useEffect(() => {
    if (query.status === "success" && query.data) {
      setCollection(query.data);
    }
  }, [query.status, query.data]);

  const handleMore = () => {
    setMenuRef(document.getElementById("collectionMoreButton"));
    setMenuVisible(true);
  };

  const onClose = () => {
    setMenuVisible(false);
    setEditVisible(false);
    setDeleteCollectionVisible(false);
    setShareVisible(false);
    setEmbedVisible(false);
  };

  const onShare = () => {
    onClose();
    setShareVisible(true);
  };

  const onEdit = () => {
    onClose();
    setEditVisible(true);
  };

  const onManage = () => {
    onClose();
    setManage(true);
  };

  const onDeleteCollection = () => {
    onClose();
    setDeleteCollectionVisible(true);
  };

  const onDeleteCollectionConfirm = () => {
    onClose();

    apiCollections.deleteCollection({
      collectionId,
    }).then(() => {
      navigate(routes.userCollections(userName));
    }).catch(() => {
      console.error("Could not delete collection.");
    });
  };

  const onEmbed = () => {
    onClose();
    setEmbedVisible(true);
  };

  const onTrash = () => {
    onClose();

    apiCollections.deleteCollectionItems({
      collectionId: collectionId,
      gifsIds: selection,
    }).then(() => {
      setManage(false);
      setSelection([]);
      window.location.reload();
    }).catch(() => {
      console.warn("Error deleting connection items.", selection);
    });
  };

  const onSelect = (ids: string[]) => {
    setSelection(ids);
  };

  const getShareLink = (): string => {
    const link = routes.collection(userName, collectionId);
    return `https://www.redgifs.com${link}`;
  };

  const embedCode = (): string => {
    if (collection) {
      const link = getShareLink();
      return `<a href="${link}">${collection.folderName} collection</a>`;
    } else {
      return ":(";
    }
  };

  if (query.isLoading) {
    return (<div className="wide page collectionItemsPage">
      <p>Loading...</p>
    </div>);
  }

  if (query.isError || !collection) {
    return (<div className="wide page collectionItemsPage">
      <p>Error loading collection.</p>
    </div>);
  }

  return (
    <Fragment>
      <div className="wide page collectionItemsPage">
        <CollectionHeader
          collection={collection}
          backLink={routes.userCollections(userName)}
          editable={isEditable}
          manage={isManage}
          more={handleMore}
          trash={onTrash}
        />

        {isManage && <div className="description">Please select the gifs you want to delete, then click the trash can buton.</div>}

        {!isManage && query.isSuccess && <div className="description">
          {collection.description ?? "This collection has no description."}
        </div>}

        {query.isSuccess && userName && collectionId && <CollectionFeed
          user={userName}
          collection={collectionId}
          showPrivate={isEditable}
          manage={isManage}
          onSelect={onSelect}
        />}

        {isMenuVisible && menuRef && <CollectionEditMenu
          anchor={menuRef}
          canShare={!!(collection && collection.published)}
          onClose={() => { setMenuVisible(false);}}
          onShare={onShare}
          onEdit={onEdit}
          onManage={onManage}
        />}

        {isEditVisible && collection && <EditCollectionPopup
          isOpen={true}
          togglePopup={onClose}
          handleDeleteCollection={onDeleteCollection}
          collection={collection}
        />}

        {isDeleteCollectionVisible && <DeleteCollectionPopup
          isOpen={true}
          togglePopup={onClose}
          handleDelete={onDeleteCollectionConfirm}
          collectionName={collection?.folderName}
        />}

        {menuRef && collection && isShareVisible && <ShareMenu
          link={getShareLink()}
          anchor={menuRef}
          onClose={onClose}
          onEmbed={onEmbed}
        />}

        {isEmbedVisible && <EmbedCodePopup
          isOpen={true}
          togglePopup={onClose}
          fixedEmbedCode={embedCode()}
          responsiveEmbedCode={embedCode()}
        />}
      </div>

      <FooterAds/>
    </Fragment>
  );
};
